import http from "../utils/http";
import { interpolate } from "../utils/string";
import { withoutAttrs } from "../utils/object";

import endpoints from "../static/endpoints";

/**
 * Get reports templates
 *
 * @param {String} reportId
 * @returns {Promise}
 */
export const getReportTemplates = async (reportId) => {
  const url = interpolate(endpoints.reportTemplates.getByReportId, {
    reportId,
  });

  const { data } = await http.get(url);

  return data.data;
};

/**
 * Upsert report note
 * @param {object} payload
 * @returns {Promise}
 */
export const upsertReportNote = async (payload) => {
  const { evokeNotes, ...rest } = payload;

  let notes = evokeNotes;
  if (!notes) {
    notes = [];
  } else {
    notes = notes.map((note) => {
      const { content, note: noteTitle } = note;

      const newContent = content.map((noteContent) =>
        withoutAttrs(noteContent, ["isDefault"])
      );

      return { note: noteTitle, content: newContent };
    });
  }

  const inputData = { ...rest, evokeNotes: notes };

  const { data } = await http.put(
    endpoints.transmission.reportNotes,
    inputData
  );

  return data;
};

/**
 * Format previous report alerts
 * @param {object} data
 * @returns {object}
 */
export const formatPreviousReportAlerts = (data) => {
  const result = [];

  const sortedData = data.sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );

  sortedData.forEach((item) => {
    const { evoke_notes, created_at, updated_at, reportId } = item || {};
    const dataToPush = { created_at, updated_at, reportId };

    if (!evoke_notes || !evoke_notes.length) {
      return;
    }

    evoke_notes.forEach((evoke_note) => {
      const { note: noteTitle, content } = evoke_note || {};
      const newContent = content
        ? content.map((content) => ({ ...content, ...dataToPush }))
        : [];
      const index = result.findIndex(
        (note) =>
          note?.title?.trim()?.toLowerCase() ===
          noteTitle?.trim()?.toLowerCase()
      );

      if (index === -1) {
        result.push({ title: noteTitle, content: newContent });
      } else {
        result[index].content.push(...newContent);
      }
    });
  });

  return result;
};

/**
 * Get Previous Report Alerts
 *
 * @param {String} reportId
 * @param {Object} params
 * @returns {Promise}
 */
export const getPreviousReportAlerts = async (reportId, params = {}) => {
  const url = interpolate(endpoints.previousAlerts, { reportId });

  const { data } = await http.get(url, { params });

  const result = formatPreviousReportAlerts(data);

  return result;
};

/**
 * Get Previous Reports
 *
 * @param {String} reportId
 * @returns {Promise}
 */
export const getPreviousReports = async (reportId) => {
  const url = interpolate(endpoints.previousReports, { reportId });

  const { data } = await http.get(url);

  return data;
};

/**
 * Get Patient Alert Banner
 *
 * @param {String} serialNumber
 * @returns {Promise}
 */
export const getPatientAlertBanner = async (serialNumber) => {
  const url = interpolate(endpoints.reportBanners, { serialNumber });

  const { data } = await http.get(url);

  return data?.data;
};

/**
 * Update Patient Alert Banner
 *
 * @param {String} serialNumber
 * @param {String} bannerText
 * @returns {Promise}
 */
export const updatePatientAlertBanner = async (serialNumber, bannerText) => {
  const url = interpolate(endpoints.reportBanners, { serialNumber });

  const payload = { patientSerialNumber: serialNumber, bannerText };

  const { data } = await http.put(url, payload);

  return data;
};

/**
 * Get Billing History
 * @param {String} serial_number
 * @returns {Promise}
 */
export const getBillingHistory = async (serial_number) => {
  const url = interpolate(endpoints.patient.billingHistory, { serial_number });

  const { data } = await http.get(url);

  return data;
};

/**
 * Upsert patient notes
 * @param {String} serialNumber
 * @param {object} payload
 * @returns {Promise}
 */
export const upsertPatientNotes = async (serialNumber, payload) => {
  const inputPayload = { serialNumber, clinicianNotes: payload };

  const { data } = await http.put(
    endpoints.transmission.patientNotes,
    inputPayload
  );

  return data;
};
